import React from "react";
import { Plus, Trash2 } from "lucide-react";

import { Button } from "../../../components/ui/button";
import { cn } from "../../../lib/utils";

export interface TierDraft {
  minPax: number | "";
  maxPax: number | "";
  price: number | "";
}

interface Props {
  tiers: TierDraft[];
  onChange: (tiers: TierDraft[]) => void;
  errors: Record<string, string>;
  maxPassengers: number;
}

const fieldClass =
  "h-11 w-full min-w-0 rounded-xl border border-white/10 bg-white/5 px-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-primary/40";

const toNumber = (value: string): number | "" => (value === "" ? "" : Number(value));

/** Faixas de passageiros com preço. Uma linha por faixa, pensada para a tela do celular. */
export default function TierEditor({ tiers, onChange, errors, maxPassengers }: Props) {
  const updateTier = (index: number, patch: Partial<TierDraft>) =>
    onChange(tiers.map((tier, i) => (i === index ? { ...tier, ...patch } : tier)));

  const addTier = () => {
    const last = tiers[tiers.length - 1];
    const nextMin = last && typeof last.maxPax === "number" ? last.maxPax + 1 : 1;
    onChange([...tiers, { minPax: nextMin, maxPax: Math.max(nextMin, maxPassengers), price: "" }]);
  };

  const removeTier = (index: number) => onChange(tiers.filter((_, i) => i !== index));

  return (
    <div className="space-y-3">
      {tiers.map((tier, index) => {
        const rowError =
          errors[`tiers.${index}.minPax`] ||
          errors[`tiers.${index}.maxPax`] ||
          errors[`tiers.${index}.price`];
        return (
          <div
            key={index}
            className={cn(
              "space-y-3 rounded-2xl border p-3",
              rowError ? "border-red-400/50 bg-red-500/5" : "border-white/10 bg-white/5"
            )}
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
                Faixa {index + 1}
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
                aria-label={`Remover faixa ${index + 1}`}
                disabled={tiers.length <= 1}
                onClick={() => removeTier(index)}
              >
                <Trash2 className="h-4 w-4" /> Remover
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-[1fr_1fr_1.5fr]">
              <label className="space-y-1">
                <span className="block text-xs text-white/60">De (passageiros)</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={100}
                  value={tier.minPax}
                  className={fieldClass}
                  onChange={(e) => updateTier(index, { minPax: toNumber(e.target.value) })}
                />
              </label>
              <label className="space-y-1">
                <span className="block text-xs text-white/60">Até (passageiros)</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={100}
                  value={tier.maxPax}
                  className={fieldClass}
                  onChange={(e) => updateTier(index, { maxPax: toNumber(e.target.value) })}
                />
              </label>
              <label className="col-span-2 space-y-1 sm:col-span-1">
                <span className="block text-xs text-white/60">Preço do trecho</span>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-white/50">
                    R$
                  </span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="1"
                    placeholder="0"
                    value={tier.price}
                    className={cn(fieldClass, "pl-10")}
                    onChange={(e) => updateTier(index, { price: toNumber(e.target.value) })}
                  />
                </div>
              </label>
            </div>
            {rowError && <p className="text-xs text-red-400">{rowError}</p>}
          </div>
        );
      })}

      {errors.tiers && <p className="text-xs text-red-400">{errors.tiers}</p>}

      <Button
        type="button"
        variant="outline"
        className="w-full border-dashed border-white/20"
        onClick={addTier}
      >
        <Plus className="h-4 w-4" /> Adicionar faixa
      </Button>
      <p className="text-xs text-white/40">
        Cada faixa define o preço do trecho para uma quantidade de passageiros (ex.: 1 a 4 e 5 a 6). As
        faixas não podem se sobrepor.
      </p>
    </div>
  );
}
